import React from "react";

// Persistent reminder shown next to every EUDAMED XML download/send action - encourages
// testing in the EUDAMED Playground before a real submission, and points users to support.
// Shared by the DataTable bulk export panel, the Basic UDI-DI popup and the product UDI-DI popup.
export const EudamedPlaygroundNotice = () => (
    <div style={{
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "#eef6fc",
        border: "1px solid #cfe4f5",
        borderRadius: "6px",
        padding: "10px 12px",
        marginTop: "12px",
        marginBottom: "12px",
        fontSize: "13px",
        color: "#31708f"
    }}>
        <strong>Before you upload to EUDAMED</strong>
        <div>
            We recommend first test uploading this file in the EUDAMED Playground to confirm it uploads correctly.
            Running into an issue? Contact us, our team will be happy to support you.
        </div>
    </div>
);

// Generates an EUDAMED XML via its generate endpoint and submits it straight to EUDAMED
// through SubmitEudamedXml (which re-validates the company's SRN + the module's security key
// and overwrites the sender identity before transmitting). Resolves with the submission
// results; rejects with an Error carrying the backend's message (e.g. "Company SRN
// (MFActorCode) is not configured") so callers can show it directly. Not usable for
// zip-producing generate endpoints - those are download-only.
export const sendEudamedXml = (generateUrl) =>
    fetch(generateUrl, { method: "GET" })
        .then(async (response) => {
            const text = await response.text();

            if (!response.ok) {
                if (response.status === 401)
                    document.dispatchEvent(new Event("api:unauthorized"));

                throw new Error(text ? text.replaceAll('"', '') : "The XML could not be generated.");
            }
            return text;
        })
        .then((xml) =>
            fetch("/umbraco/api/RIMSProducts/SubmitEudamedXml", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(xml),
            })
        )
        .then(async (response) => {
            if (!response.ok) {
                if (response.status === 401)
                    document.dispatchEvent(new Event("api:unauthorized"));

                const text = await response.text();
                throw new Error(text ? text.replaceAll('"', '') : "The submission to EUDAMED failed.");
            }
            return response.json();
        });

const triggerBlobDownload = (blob, filename) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
};

// GET's a generate endpoint and reads whether XmlFile/ZipXmlFiles (RIMSProductsController)
// flagged the result with the X-Eudamed-Srn-Missing header - set when a payload's sender
// nodeActorCode fell back to the "fill this in" placeholder because it isn't linked to a Basic
// UDI-DI (or that Basic UDI-DI's own SRN isn't filled in) and no senderActorCode override was
// supplied either - and/or the X-Eudamed-Warnings header (a JSON array of strings, e.g. a GS1 DI
// code that isn't a well-formed GTIN-14, or a Storage/Handling Condition missing its required
// comment - see EudamedXml.FindWarnings). Neither blocks the download; both are meant for the
// frontend to show next to it, not baked into the file. Doesn't touch the browser's download UI -
// see downloadEudamedXmlFile.
const fetchEudamedXml = (url) =>
    fetch(url, { method: "GET" })
        .then(async (response) => {
            if (!response.ok) {
                if (response.status === 401)
                    document.dispatchEvent(new Event("api:unauthorized"));

                const text = await response.text();
                throw new Error(text ? text.replaceAll('"', '') : "The XML could not be generated.");
            }

            let warnings = [];
            try {
                warnings = JSON.parse(response.headers.get("X-Eudamed-Warnings") || "[]");
            } catch { }

            return {
                blob: await response.blob(),
                missingSrn: response.headers.get("X-Eudamed-Srn-Missing") === "true",
                warnings,
            };
        });

// Fetches a generated EUDAMED XML (or zip of XMLs) and hands it to the browser as a download.
// If the backend couldn't resolve a sender SRN on its own (X-Eudamed-Srn-Missing - see
// fetchEudamedXml), prompts once for one and re-requests with it appended as
// &senderActorCode=...; if the prompt is left blank/cancelled, downloads the placeholder version
// anyway. Resolves { missingSrn, warnings } - see describeDownloadWarnings for turning that into a
// single message. Rejects with an Error whose message is the backend's BadRequest body (the
// actionable validation message from the Eudamed builders), so callers can show it directly.
// Shared by DataTable's export popup (per selected products) and the Basic UDI details
// pane's Register/Update buttons (per Basic UDI-DI).
export const downloadEudamedXmlFile = (url, filename) =>
    fetchEudamedXml(url).then(({ blob, missingSrn, warnings }) => {
        if (!missingSrn) {
            triggerBlobDownload(blob, filename);
            return { missingSrn: false, warnings };
        }

        const srn = window.prompt(
            "This file's sender identity (SRN) could not be determined automatically - it isn't linked to a Basic UDI-DI with an SRN on file.\n\n" +
            "Enter the EUDAMED SRN to use for this file (e.g. XX-MF-000000000), or leave this blank to fill it in yourself before uploading to EUDAMED:"
        );
        const trimmedSrn = srn ? srn.trim() : "";

        if (!trimmedSrn) {
            triggerBlobDownload(blob, filename);
            return { missingSrn: true, warnings };
        }

        const retryUrl = url + (url.includes("?") ? "&" : "?") + "senderActorCode=" + encodeURIComponent(trimmedSrn);
        return fetchEudamedXml(retryUrl).then(({ blob: retryBlob, missingSrn: stillMissing, warnings: retryWarnings }) => {
            triggerBlobDownload(retryBlob, filename);
            return { missingSrn: stillMissing, warnings: retryWarnings };
        });
    });

// Combines downloadEudamedXmlFile's two non-blocking, post-download notices ({ missingSrn,
// warnings }) into one message, for the callers that show it via a single notice/error slot
// (setEudamedXmlError) rather than two separate UI elements. Returns null when there's nothing to
// show.
export const describeDownloadWarnings = ({ missingSrn, warnings }) => {
    const parts = [];
    if (missingSrn)
        parts.push("This file's nodeActorCode (SRN) still needs to be filled in by hand before uploading it to EUDAMED.");
    if (warnings && warnings.length > 0)
        parts.push(...warnings);

    // One per line (rendered with white-space: pre-line) so several notices don't run together.
    return parts.length > 0 ? parts.join("\n") : null;
};
